import { BaseProps } from '../props/base/base-props';
import { ShapeProps } from '../props/shape/shape-props';
import { cloneDeep } from '../utils/clone-deep';
import { DataType } from '../data';
import { ChartBaseProps } from '../props/shape/chart/chart-base-props';
import { ChartProps } from '../props/shape/chart/chart-props';
import { ChartContext } from '../context/chart-context';
import { TableProps } from '../props/shape/table/table-props';
import { PictureProps } from '../props/shape/picture-props';
import { SmartArtProps } from '../props/shape/smart-art-props';
import { EffectListProps } from '../props/common/effect-list-props';
import { FillProps } from '../props/common/fill-props';
import { ColorProps } from '../props/common/color-props/color-props';
import { LineProps } from '../props/common/line-props';
import { ThemeContext } from '../context/theme-context';
import type {
    ICTEffectList,
    ICTFontCollection,
    ICTLineProperties,
    ICTPlaceholderPml,
    ICTShapeStyle,
    IEGColorChoice,
    IEGFillProperties,
} from '@tencent/slide-sdk-pb-model';
import type { IPlaceholderShapeData } from '../data';
import type { IPageDataModel } from '../model/page-data-model';
import type { DeepReadonly } from 'ts-essentials';
export interface IDataHelper {
    /**
     * 给定图形的原始数据以及 page，返回继承后的图形数据
     *
     * 使用参考：选择性粘贴占位符时使用目标主题
     * shapeData - 复制的占位符的 ownedData
     * page - 复制目标页
     * ph - 如果 shapeData 里有 ph 就不用填
     * 返回使用目标主题的完整数据
     *
     * @param shapeData 原始图形数据
     * @param page 样式继承的目标页
     * @param ph 自定义占位符属性，若存在，将覆盖 data 里原有的 ph 属性
     * @returns 继承后的数据
     */
    getInheritedDataFromPlaceholder(
        shapeData: IPlaceholderShapeData,
        page: IPageDataModel,
        ph?: ICTPlaceholderPml,
    ): DeepReadonly<IPlaceholderShapeData>;

    /**
     * 获取预设的快速样式
     * @param style 元素的快速样式原始数据
     * @param page 元素所在页的 sdk model
     * @returns 主题继承后的快速样式数据
     */
    getInheritedFastStyle(style: ICTShapeStyle, page: IPageDataModel): IStyleProps;
}

export interface IStyleProps {
    effectList?: DeepReadonly<ICTEffectList>;
    fill?: DeepReadonly<IEGFillProperties>;
    font?: DeepReadonly<ICTFontCollection>;
    fontColor?: DeepReadonly<IEGColorChoice>;
    line?: DeepReadonly<ICTLineProperties>;
}

export class DataHelper implements IDataHelper {
    public getInheritedDataFromPlaceholder(
        shapeData: IPlaceholderShapeData,
        page: IPageDataModel,
        ph?: ICTPlaceholderPml,
    ): DeepReadonly<IPlaceholderShapeData> {
        const ownedData = cloneDeep(shapeData) as IPlaceholderShapeData;
        if (ph) {
            ownedData.nvPr.ph = ph;
        }

        switch (ownedData.type) {
            case DataType.Shape: {
                const props = new ShapeProps(ownedData, page.getPageContext());
                return BaseProps.getInheritedData(props);
            }
            case DataType.GraphicalObjectFrameChart: {
                const props = new ChartProps(ownedData, new ChartContext(page.getPageContext()));
                return ChartBaseProps.getInheritedData(props);
            }
            case DataType.GraphicalObjectFrameTable: {
                const props = new TableProps(ownedData, page.getPageContext());
                return BaseProps.getInheritedData(props);
            }
            case DataType.Picture: {
                const props = new PictureProps(ownedData, page.getPageContext());
                return BaseProps.getInheritedData(props);
            }
            case DataType.GraphicalObjectFrameSmartArt: {
                const props = new SmartArtProps(ownedData, page.getPageContext());
                return BaseProps.getInheritedData(props);
            }
        }
    }

    public getInheritedFastStyle(style: ICTShapeStyle, page: IPageDataModel): IStyleProps {
        const resultStyle: IStyleProps = {};
        if (style.effectRef) {
            const { idx, egColorChoice } = style.effectRef;
            const data = page.getPageContext().getThemeContext().getEffectListStyleByIndex(idx);
            if (data) {
                resultStyle.effectList = new EffectListProps(data, page.getPageContext(), egColorChoice);
            }
        }
        if (style.fillRef) {
            const { idx, egColorChoice } = style.fillRef;
            const data = page.getPageContext().getThemeContext().getFillStyleByIndex(idx);
            if (data) {
                resultStyle.fill = new FillProps(data, page.getPageContext(), { placeholderColor: egColorChoice });
            }
        }
        if (style.fontRef) {
            const { idx, egColorChoice } = style.fontRef;
            const fontCollection = page.getPageContext().getThemeContext().getFontCollectionStyleByIndex(idx);
            if (egColorChoice) {
                resultStyle.fontColor = new ColorProps(egColorChoice, page.getPageContext());
                resultStyle.font = ThemeContext.getFontStyleByFontCollection(fontCollection);
            }
        }
        if (style.lnRef) {
            const { idx, egColorChoice } = style.lnRef;
            const data = page.getPageContext().getThemeContext().getLineStyleByIndex(idx);
            if (data) {
                resultStyle.line = new LineProps(data, page.getPageContext(), egColorChoice);
            }
        }
        return resultStyle;
    }
}
